import { AlertCircle, CheckCircle2, FileArchive, ImageIcon, Loader2, RotateCcw, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Upload queue modal (#4784).
 *
 * One row per dropped/selected file with an independent outcome: a pre-check or upload
 * failure on one file never hides or blocks the others, and the reason sits inline on the
 * failed row instead of in a toast. The modal is feedback only — uploads continue if it is
 * closed mid-run; the owning component (ImageGallery) drives the actual upload loop and
 * feeds row state in via props.
 */

export type UploadQueueStatus = 'waiting' | 'uploading' | 'done' | 'failed';

export interface UploadQueueItem {
  id: string;
  filename: string;
  byteSize: number;
  isZip: boolean;
  status: UploadQueueStatus;
  /** Failure reason, or a success note (e.g. ZIP import counts). */
  detail?: string;
  /** Individual ZIP entries that were skipped, shown as a sub-list under the ZIP row. */
  skippedEntries?: Array<{ filename: string; reason: string }>;
}

interface UploadQueueModalProps {
  open: boolean;
  onClose: () => void;
  items: UploadQueueItem[];
  onRetry: (id: string) => void;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

const ROW_STYLES: Record<UploadQueueStatus, { row: string; iconBg: string; iconColor: string }> = {
  waiting:  { row: '',                        iconBg: 'bg-gray-100',        iconColor: 'text-gray-400' },
  uploading:{ row: 'bg-[#290BD9]/[0.03]',     iconBg: 'bg-[#290BD9]/10',    iconColor: 'text-[#290BD9]' },
  done:     { row: 'bg-emerald-50/60',         iconBg: 'bg-emerald-100',     iconColor: 'text-emerald-600' },
  failed:   { row: 'bg-red-50/60',             iconBg: 'bg-red-100',         iconColor: 'text-red-500' },
};

const StatusIcon = ({ status, isZip }: { status: UploadQueueStatus; isZip: boolean }) => {
  if (status === 'uploading') return <Loader2 className='h-4 w-4 animate-spin' strokeWidth={2} />;
  if (status === 'done')      return <CheckCircle2 className='h-4 w-4' strokeWidth={2} />;
  if (status === 'failed')    return <XCircle className='h-4 w-4' strokeWidth={2} />;
  return isZip
    ? <FileArchive className='h-4 w-4' strokeWidth={1.5} />
    : <ImageIcon className='h-4 w-4' strokeWidth={1.5} />;
};

const QueueRow = ({ item, onRetry }: { item: UploadQueueItem; onRetry: (id: string) => void }) => {
  const styles = ROW_STYLES[item.status];
  const hasSkips = item.skippedEntries && item.skippedEntries.length > 0;

  return (
    <div className={`rounded-lg mb-2 last:mb-0 overflow-hidden border ${item.status === 'waiting' ? 'border-gray-100' : item.status === 'uploading' ? 'border-[#290BD9]/20' : item.status === 'done' ? 'border-emerald-200' : 'border-red-200'} ${styles.row}`}>
      <div className='flex items-center gap-3 px-3 py-3'>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${styles.iconBg} ${styles.iconColor}`}>
          <StatusIcon status={item.status} isZip={item.isZip} />
        </div>

        <div className='min-w-0 flex-1'>
          <p className='truncate text-sm font-semibold text-gray-900 leading-tight' title={item.filename}>
            {item.filename}
          </p>
          <p className={`text-xs mt-0.5 leading-tight ${item.status === 'failed' ? 'text-red-600' : item.status === 'done' ? 'text-emerald-700' : item.status === 'uploading' ? 'text-[#290BD9]' : 'text-gray-400'}`}>
            {item.status === 'uploading' && 'Uploading…'}
            {item.status === 'waiting'   && formatBytes(item.byteSize)}
            {item.status === 'done'      && (item.detail ?? formatBytes(item.byteSize))}
            {item.status === 'failed'    && (item.detail ?? 'Upload failed.')}
          </p>
        </div>

        {item.status === 'failed' && (
          <Button
            variant='ghost'
            size='sm'
            className='h-7 w-7 p-0 shrink-0 text-gray-400 hover:text-[#290BD9] hover:bg-[#290BD9]/10'
            onClick={() => onRetry(item.id)}
            title='Retry upload'
          >
            <RotateCcw className='h-3.5 w-3.5' />
          </Button>
        )}
      </div>

      {hasSkips && (
        <div className='mx-3 mb-3 rounded-md bg-amber-50 border border-amber-200 overflow-hidden'>
          <div className='px-3 py-1.5 border-b border-amber-200 flex items-center gap-1.5'>
            <AlertCircle className='h-3 w-3 text-amber-500 shrink-0' />
            <span className='text-xs font-semibold text-amber-700'>
              {item.skippedEntries!.length} file{item.skippedEntries!.length === 1 ? '' : 's'} skipped
            </span>
          </div>
          <div className='divide-y divide-amber-100'>
            {item.skippedEntries!.map((entry) => (
              <div key={entry.filename} className='px-3 py-1.5 flex items-center gap-2'>
                <span className='text-xs font-medium text-gray-700 truncate flex-1' title={entry.filename}>
                  {entry.filename}
                </span>
                <span className='text-xs text-amber-600 shrink-0'>{entry.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const UploadQueueModal = ({ open, onClose, items, onRetry }: UploadQueueModalProps) => {
  const doneCount   = items.filter((i) => i.status === 'done').length;
  const failedCount = items.filter((i) => i.status === 'failed').length;
  const isBusy      = items.some((i) => i.status === 'uploading' || i.status === 'waiting');
  const progress    = items.length > 0 ? Math.round(((doneCount + failedCount) / items.length) * 100) : 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Importing Images</DialogTitle>
          <DialogDescription>
            {isBusy
              ? 'Uploads continue if this window is closed.'
              : failedCount > 0
                ? `${doneCount} succeeded · ${failedCount} failed`
                : `${doneCount} image${doneCount === 1 ? '' : 's'} ready`}
          </DialogDescription>
        </DialogHeader>

        {items.length > 0 && (
          <div className='h-1 rounded-full bg-gray-100 overflow-hidden -mt-1'>
            <div
              className='h-full rounded-full bg-[#290BD9] transition-all duration-500'
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        <div className='max-h-80 overflow-y-auto pr-0.5'>
          {items.map((item) => (
            <QueueRow key={item.id} item={item} onRetry={onRetry} />
          ))}
        </div>

        <DialogFooter className='sm:justify-end'>
          <Button variant='outline' className='text-gray-900 h-9' onClick={onClose}>
            {isBusy ? 'Hide' : 'Done'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
